import { comparing } from 'core/utils/sort'
import { listToObj } from 'core/utils/common'
import { WebInterface } from 'core/constants/web-interfaces'
import { IAPIAggregate, ICexAccount } from 'core/api/rest/queries'
import { IFormattedAPIData } from './accounts'
import { IAggregateOrderMapping } from './aggregates'

export type Template = {
  is_default: boolean
  owner: string
  label: string
  account_hierarchy: IAccountHierarchy[] | null
  is_super_default?: boolean
  is_editable?: boolean
  is_owner?: boolean
  is_visible?: boolean
  web_interface_list?: WebInterface[]
}

export interface ITemplate {
  id: number
  label: string
  is_super_default?: boolean
  is_default?: boolean
  is_copy?: boolean
  is_visible?: boolean
  owner?: string
  account_hierarchy: IAccountHierarchy[]
  aggregate_hierarchy?: IAggregateHierarchy[]
  group_hierarchy?: []
  web_interface_list: WebInterface[]
  is_owner?: boolean
  is_editable?: boolean
}

export interface IAccountHierarchy {
  account: string
  index: number
  children?: IAccountChild[]
  is_hidden?: boolean
  isHiddenForComputation?: boolean
}

export interface IAccountChild {
  account: string
  index: number
}

export interface IAggregateHierarchy {
  id: number
  aggregate: string
  index: number
  is_spatial_computation: boolean
  is_hidden?: boolean
  is_toggleable?: boolean
  label_code: string
  can_select_year?: boolean
  from: string
  to: string
  comparison_allowed?: boolean
}

export const TEMPLATE_NAME = {
  ANNUAL: 'Annual Initialization Template',
  INSTORE_PERFORMANCE: 'Instore Performance',
  DEBRIEF_DEPARTMENT_MANAGER: '1. Debrief Department Manager'
}

export type TemplateName = (typeof TEMPLATE_NAME)[keyof typeof TEMPLATE_NAME]

/**
 * Get accounts
 * @param template
 * @return accounts
 */
const getAccounts = (template: ITemplate): IAccountHierarchy[] => template.account_hierarchy

/**
 * Get aggregates
 * @param template
 * @return aggregates
 */
const getAggregates = (template: ITemplate): IAggregateHierarchy[] => template.aggregate_hierarchy

/**
 * Get template selected with filter
 * @return template
 * @param template
 * @param dataItems
 * @param aggregatesOrderMapping
 */
export const getTemplateHierarchy = (
  template: ITemplate,
  dataItems: IFormattedAPIData<IAPIAggregate | ICexAccount>,
  aggregatesOrderMapping?: IAggregateOrderMapping
): ITemplate => {
  const accounts = getAccounts(template)
  const accountHierarchy = listToObj(accounts, 'account')
  // Fix MG-3893 Cannot read properties of undefined (reading 'length')
  const accountHierarchyListLength = (accounts && accounts.length) || 0
  const accountHierarchyConsolidated = []

  Object.keys(dataItems).forEach((item, index) => {
    const currentAccount = dataItems[item]
    const isAggregate = 'account_values' in currentAccount
    const accountReference = accountHierarchy[currentAccount.label_code]

    // if template accounts hierarchy contains current account
    if (accountReference) {
      accountHierarchyConsolidated.push(accountReference)
      // else if is not an aggregate
    } else if (!isAggregate) {
      accountHierarchyConsolidated.push({
        account: currentAccount.label_code,
        index: index + accountHierarchyListLength,
        children: (currentAccount as ICexAccount).children
          ? (currentAccount as ICexAccount).children.map((child, index) => ({
              account: child,
              index
            }))
          : [],
        isHiddenForComputation: true
      })
    }
  })

  const aggregate_hierarchy = []

  getAggregates(template).forEach((aggregate, aggregateIndex) => {
    aggregate_hierarchy.push({
      ...aggregate,
      index: aggregatesOrderMapping ? aggregatesOrderMapping[aggregate.aggregate] : aggregateIndex + 1
    })
  })

  return {
    ...template,
    account_hierarchy: accountHierarchyConsolidated,
    aggregate_hierarchy
  }
}

export const sortAccountHierarchy = template => {
  if (!template) return null
  const accountHierarchyReSorted: IAccountHierarchy[] = []
  template.account_hierarchy.forEach((account: IAccountHierarchy, index: number) => {
    const accountHierarchy: IAccountHierarchy = { ...account }
    accountHierarchy.index = index + 1
    accountHierarchy.children = []
    account.children.sort(comparing(it => it.index)).forEach((childAccount: IAccountChild, childIndex: number) => {
      const childAccountCopy: IAccountChild = { ...childAccount }
      childAccountCopy.index = childIndex + 1
      accountHierarchy.children.push(childAccountCopy)
    })
    accountHierarchyReSorted.push(accountHierarchy)
  })

  return accountHierarchyReSorted
}
